import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, ShieldOff, Copy, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AdminPage } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { formatUSD } from "@/lib/format";

const genTempPassword = () => "Belo!" + Math.random().toString(36).slice(-8).toUpperCase();

const AdminDealerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dealer, setDealer] = useState(null);
  const [dealerUsers, setDealerUsers] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [tempPasswordShown, setTempPasswordShown] = useState(null);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateName, setDeactivateName] = useState("");
  const [userForm, setUserForm] = useState({ email: "", full_name: "", phone: "", temporary_password: "" });

  const reload = () => {
    api.get("/admin/dealers").then((r) => setDealer(r.data.find((d) => d.id === id) || null));
    api.get(`/admin/dealers/${id}/users`).then((r) => setDealerUsers(r.data)).catch(() => setDealerUsers([]));
  };
  useEffect(() => { reload(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDealer = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/dealers/${id}`, patch);
      toast.success("Dealer updated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitAddUser = async (e) => {
    e.preventDefault();
    const tp = userForm.temporary_password || genTempPassword();
    try {
      await api.post(`/admin/dealers/${id}/users`, { ...userForm, temporary_password: tp });
      setTempPasswordShown({ email: userForm.email, password: tp });
      toast.success("Dealer user created");
      setUserForm({ email: "", full_name: "", phone: "", temporary_password: "" });
      setAddUserOpen(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doDeactivate = async () => {
    if (deactivateName !== dealer?.name) return;
    try {
      await api.delete(`/admin/dealers/${id}`);
      toast.success("Dealer deactivated. Their vehicles have been removed from the marketplace.");
      navigate("/admin/dealers");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!dealer) return <AdminPage title="Loading…" testId="admin-dealer-detail-page"><div className="h-96 bg-slate-100 rounded-2xl animate-pulse" /></AdminPage>;

  return (
    <AdminPage
      title={dealer.name}
      subtitle={`${dealer.city}, ${dealer.state} · slug "${dealer.slug}"`}
      testId="admin-dealer-detail-page"
      action={<Button asChild variant="outline"><Link to="/admin/dealers"><ArrowLeft size={13} /> Back</Link></Button>}
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Active listings</p><p className="mt-1 font-display text-2xl">{dealer.active_listings}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Leads 30d</p><p className="mt-1 font-display text-2xl">{dealer.total_leads_30d}</p></div>
        <div className="bg-white border border-slate-200 rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest text-slate-500">Showed up 30d</p><p className="mt-1 font-display text-2xl text-emerald-700">{dealer.showed_up_30d}</p></div>
        <div className="bg-gold text-white rounded-2xl p-4"><p className="text-[10px] uppercase tracking-widest">Billing MTD</p><p className="mt-1 font-display text-2xl">{formatUSD(dealer.estimated_billing_this_month, 0)}</p></div>
      </div>

      <DealerEditor dealer={dealer} onSave={updateDealer} saving={saving} />

      {/* Dealer users */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dealer users</p>
            <p className="text-sm text-slate-600">Portal accounts attached to this dealership.</p>
          </div>
          <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => setAddUserOpen(true)} data-testid="admin-add-dealer-user-btn">
            <Plus size={14} /> Add dealer user
          </Button>
        </div>
        {!dealerUsers ? <div className="mt-4 h-16 bg-slate-100 rounded animate-pulse" /> :
          dealerUsers.length === 0 ? <p className="mt-4 text-sm text-slate-400">No dealer users yet.</p> :
          <table className="mt-4 w-full text-sm">
            <thead className="text-slate-500 text-[11px] uppercase tracking-widest">
              <tr><th className="text-left py-2">Name</th><th className="text-left">Email</th><th className="text-left">Phone</th><th className="text-left">Created</th><th className="text-left">Force PW change</th><th></th></tr>
            </thead>
            <tbody>
              {dealerUsers.map((u) => (
                <tr key={u.id} className="border-t border-slate-100" data-testid={`admin-dealer-user-${u.id}`}>
                  <td className="py-2 font-medium">{u.full_name} {u.is_active === false && <span className="ml-2 text-[10px] uppercase text-rose-600">deactivated</span>}</td>
                  <td className="py-2 text-slate-600">{u.email}</td>
                  <td className="py-2 text-slate-600">{u.phone || "—"}</td>
                  <td className="py-2 text-xs text-slate-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="py-2">{u.force_password_change ? <span className="text-[11px] font-semibold text-amber-600">Yes</span> : <span className="text-slate-400">No</span>}</td>
                  <td className="py-2 text-right">
                    {u.is_active !== false && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-slate-600 hover:bg-slate-100 mr-1"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(`/admin/dealers/${id}/users/${u.id}/reset-password`, {});
                              setTempPasswordShown({ email: u.email, password: data.temp_password });
                              toast.success("Password reset. Share the temp password with the user.");
                              reload();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          data-testid={`admin-dealer-user-reset-password-${u.id}`}
                          title="Reset password"
                        >
                          <KeyRound size={12} /> Reset PW
                        </Button>
                        <Button variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50" onClick={async () => {
                          await api.delete(`/admin/dealers/${id}/users/${u.id}`);
                          toast.success("User deactivated");
                          reload();
                        }} data-testid={`admin-dealer-user-deactivate-${u.id}`}>Deactivate</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>

      <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-5">
        <p className="text-sm font-semibold text-rose-700">Danger zone</p>
        <p className="text-xs text-slate-700 mt-1">Deactivating this dealer will hide their {dealer.active_listings} vehicles from the marketplace immediately. Leads and appointment history are preserved.</p>
        <Button variant="outline" className="mt-3 border-rose-300 text-rose-700 hover:bg-rose-100" onClick={() => setDeactivateOpen(true)} data-testid="admin-deactivate-dealer-btn">
          <ShieldOff size={14} /> Deactivate dealership
        </Button>
      </div>

      {/* Add dealer user dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent data-testid="admin-add-dealer-user-dialog">
          <DialogHeader>
            <DialogTitle>New dealer user for {dealer.name}</DialogTitle>
            <DialogDescription>They'll receive a temp password below — share it privately. They'll be prompted to change it on first login.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAddUser} className="space-y-3">
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Full name</Label><Input required value={userForm.full_name} onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })} data-testid="admin-add-dealer-user-name" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Email</Label><Input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} data-testid="admin-add-dealer-user-email" /></div>
            <div><Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label><Input value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} /></div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">Temporary password</Label>
              <div className="flex gap-2">
                <Input value={userForm.temporary_password} onChange={(e) => setUserForm({ ...userForm, temporary_password: e.target.value })} placeholder="Auto-generated if empty" data-testid="admin-add-dealer-user-password" />
                <Button type="button" variant="outline" onClick={() => setUserForm({ ...userForm, temporary_password: genTempPassword() })}>Generate</Button>
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline">Cancel</Button></DialogClose>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-add-dealer-user-submit">Create user</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Temp password display */}
      <Dialog open={!!tempPasswordShown} onOpenChange={() => setTempPasswordShown(null)}>
        <DialogContent data-testid="admin-temp-password-dialog">
          <DialogHeader>
            <DialogTitle>User created — copy this now</DialogTitle>
            <DialogDescription>This is the only time the temporary password will be shown. Share it privately.</DialogDescription>
          </DialogHeader>
          {tempPasswordShown && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono text-sm">
              <p><span className="text-slate-500">Email:</span> {tempPasswordShown.email}</p>
              <p><span className="text-slate-500">Password:</span> <span className="font-bold" data-testid="admin-temp-password-value">{tempPasswordShown.password}</span></p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => { navigator.clipboard?.writeText(`${tempPasswordShown?.email} / ${tempPasswordShown?.password}`); toast.success("Copied to clipboard"); }}>
              <Copy size={13} /> Copy
            </Button>
            <DialogClose asChild><Button variant="outline">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm-by-name deactivate */}
      <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <DialogContent data-testid="admin-deactivate-dealer-dialog">
          <DialogHeader>
            <DialogTitle>Deactivate {dealer.name}?</DialogTitle>
            <DialogDescription>Type the dealership name below to confirm. This hides {dealer.active_listings} vehicles from the marketplace.</DialogDescription>
          </DialogHeader>
          <Input placeholder={dealer.name} value={deactivateName} onChange={(e) => setDeactivateName(e.target.value)} data-testid="admin-deactivate-dealer-name-input" />
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={deactivateName !== dealer.name} onClick={doDeactivate} data-testid="admin-deactivate-dealer-confirm">
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPage>
  );
};

const DealerEditor = ({ dealer, onSave, saving }) => {
  const [f, setF] = useState({});
  useEffect(() => { setF({}); }, [dealer?.id]);
  const patch = (k, v) => setF({ ...f, [k]: v });
  const val = (k) => (k in f ? f[k] : (dealer[k] ?? ""));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Dealer profile</p>
      <div className="mt-3 grid md:grid-cols-2 gap-3">
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">Name</Label><Input value={val("name")} onChange={(e) => patch("name", e.target.value)} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">Contact email</Label><Input value={val("email")} onChange={(e) => patch("email", e.target.value)} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">Address</Label><Input value={val("address")} onChange={(e) => patch("address", e.target.value)} /></div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs uppercase tracking-widest text-slate-500">City</Label><Input value={val("city")} onChange={(e) => patch("city", e.target.value)} /></div>
          <div><Label className="text-xs uppercase tracking-widest text-slate-500">State</Label><Input maxLength={2} value={val("state")} onChange={(e) => patch("state", e.target.value.toUpperCase())} /></div>
          <div><Label className="text-xs uppercase tracking-widest text-slate-500">ZIP</Label><Input value={val("zip")} onChange={(e) => patch("zip", e.target.value)} /></div>
        </div>
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">Phone</Label><Input value={val("phone")} onChange={(e) => patch("phone", e.target.value)} /></div>
        <div><Label className="text-xs uppercase tracking-widest text-slate-500">Website</Label><Input value={val("website")} onChange={(e) => patch("website", e.target.value)} /></div>
      </div>
      <div className="mt-3">
        <Label className="text-xs uppercase tracking-widest text-slate-500">Description</Label>
        <Textarea rows={3} value={val("description")} onChange={(e) => patch("description", e.target.value)} />
      </div>
      <div className="mt-4 flex justify-end">
        <Button disabled={saving || Object.keys(f).length === 0} onClick={() => onSave(f)} className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-dealer-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
};

export default AdminDealerDetail;
